import { type Dictionary } from '@ngrx/entity';

import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
} from '@blaze-runtime/engine-domain';
import {
  type FormListErrors,
  type FormValidationErrors,
  type FormValidationMessages,
  isBindingActive,
  type ValidationRules,
} from '@blaze-shared/elements-domain';
import { type ColumnLike } from '@blaze-shared/schema-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import {
  hasDataProps,
  hasWrapperRefs,
} from '../../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import { getWizardSteps } from '../../../elements/wizard/wizard.utils';
import { isElementChildOfAtSlot } from '../../../utils/layout/relationship';

/** One form's errors, already rendered, as reported by a form group or wizard step. */
export interface FormNotification {
  formId: CanvasElementId;
  notifications: string;
}

/** Turns a field's broken rules into the messages its element renders. */
export function mapValidationErrorsToMessages(
  errors: FormValidationErrors | undefined,
): FormValidationMessages | undefined {
  if (!errors) {
    return undefined;
  }

  return Object.entries(errors).reduce((messages, entry) => {
    const [rule, state] = entry as [ValidationRules, boolean];
    if (!state) {
      return messages;
    }

    switch (rule) {
      case 'required':
        messages.required = 'Required field';
        break;
      case 'valueFormat':
        messages.valueFormat = 'Wrong value format';
        break;
      case 'endAfterStart':
        messages.endAfterStart = 'End date should be after start date';
        break;
      case 'min':
        messages.min = `Value should be greater than or equal to: ${state}`;
        break;
      case 'max':
        messages.max = `Value should be lower than or equal to: ${state}`;
        break;
      case 'minRows':
        messages.minRows = `The form should have at least ${state} ${pluralizeRows(state)}`;
        break;
      case 'maxRows':
        messages.maxRows = `The form should have at most ${state} ${pluralizeRows(state)}`;
        break;
      case 'invalid':
        messages.invalid = state as unknown as string;
        break;
      default:
        break;
    }
    return messages;
  }, {} as FormValidationMessages);
}

/**
 * Describes a formlike container's errors as the HTML shown when its commit is rejected.
 * Only the first broken field (and, for a list, the first broken row) is named, to keep
 * the notification short.
 *
 * @param getColumnName resolves a bound column to the name the schema knows it by
 */
export function describeContainerErrors(
  containerId: CanvasElementId,
  containerType: 'formContainer' | 'editableList' | 'formDetails',
  errors: Dictionary<FormValidationErrors> | Dictionary<FormListErrors> | undefined,
  elements: Dictionary<CanvasElement>,
  getColumnName: (column: ColumnLike) => string,
): string {
  if (!errors) {
    return '';
  }

  const form = elements[containerId];
  if (!form || !hasWrapperRefs(form)) {
    return '';
  }

  const fieldLabels = collectFieldLabels(form.props.wrapper.refs, elements, getColumnName);

  if (containerType === 'formContainer') {
    return Object.entries(errors as Dictionary<FormValidationErrors>)
      .slice(0, 1)
      .reduce((result, [field, fieldErrors]) => {
        if (!fieldErrors) {
          return result;
        }
        return result + describeFieldErrors(fieldLabels[field] ?? field, fieldErrors);
      }, '');
  }

  const rowErrors = Object.values(errors as Dictionary<FormListErrors>).sort(
    (x, y) => (x?.displayIndex ?? 0) - (y?.displayIndex ?? 0),
  );
  return rowErrors.slice(0, 1).reduce((result, rowError) => {
    if (!rowError || rowError.valid) {
      return result;
    }

    const rowInfix = ` in row #<b>${rowError.displayIndex + 1}</b>`;
    for (const [field, fieldErrors] of Object.entries(rowError.errors).slice(0, 1)) {
      result += describeFieldErrors(fieldLabels[field] ?? field, fieldErrors, rowInfix);
    }
    return result;
  }, '');
}

/**
 * Joins the already-described errors of the forms a group commits at once. A wizard groups
 * them by the step the form sits in, so the user knows where to go back to.
 */
export function describeGroupedFormErrors(
  wizard: CanvasElementFrom<'wizard'> | undefined,
  formNotifications: FormNotification[],
  elements: Dictionary<CanvasElement>,
): string {
  const notifications = formNotifications.filter(({ notifications }) => notifications);

  if (!wizard) {
    return describeFormGroupErrors(notifications, elements);
  }

  const stepNotifications = getWizardSteps(wizard.props).map((step, index) => ({
    step: `${index + 1}. ${step.label}`,
    notifications: notifications.filter(({ formId }) =>
      isElementChildOfAtSlot(elements, formId, wizard.id, wizard.slots.ids[index]),
    ),
  }));
  const steps = (stepNotifications || [])
    .filter(({ notifications }) => notifications.length > 0)
    .map(
      ({ step, notifications }) =>
        `<li>Errors in step <i>${step}</i>${describeFormGroupErrors(notifications, elements)}</li>`,
    );

  return `<ul class="wizard-validation">${steps.join('')}</ul>`;
}

function describeFormGroupErrors(
  notifications: FormNotification[],
  elements: Dictionary<CanvasElement>,
): string {
  const items = notifications.map(({ formId, notifications }) => {
    const form = elements[formId];
    if (!isElementType(form, 'formContainer') && !isElementType(form, 'editableList')) {
      throw new Error('invalid form type');
    }
    const dataProps = hasDataProps(form) ? form.props.data : [];
    const label =
      form.props.content.title?.label ||
      dataProps.find(({ key }) => key === 'value')?.dataSource?.name;
    return `<li><i>${label}</i> form has errors:<br />${notifications}</li>`;
  });

  return `<ul class="form-group-validation">${items.join('')}</ul>`;
}

/** Describes the first rule a field breaks; `messageInfix` locates it (e.g. a list row). */
function describeFieldErrors(
  fieldName: string,
  errors: FormValidationErrors | undefined,
  messageInfix = '',
): string {
  if (!errors) {
    return '';
  }

  return Object.entries(errors)
    .slice(0, 1)
    .reduce((result, entry) => {
      const [rule, state] = entry as [ValidationRules, boolean];
      if (state === undefined) {
        return result;
      }

      const field = `"<b>${fieldName}</b>"${messageInfix}`;
      switch (rule) {
        case 'required':
          return result + `Field ${field} is required<br>`;
        case 'valueFormat':
          return result + `Format for field ${field} is invalid<br>`;
        case 'endAfterStart':
          return `End date in the field ${field} should be after start date`;
        case 'min':
          return `Field ${field} value should be greater than or equal to: ${state}`;
        case 'max':
          return `Field ${field} value should be lower than or equal to: ${state}`;
        case 'minRows':
          return `The form should have at least ${state} ${pluralizeRows(state)}`;
        case 'maxRows':
          return `The form should have at most ${state} ${pluralizeRows(state)}`;
        case 'invalid':
          return result + `Field ${field}: ${state}<br>`;
        default:
          return result;
      }
    }, '');
}

/** Maps each bound field of a form to the label its element shows. */
function collectFieldLabels(
  refs: Record<string, CanvasElementId>,
  elements: Dictionary<CanvasElement>,
  getColumnName: (column: ColumnLike) => string,
): Record<string, string> {
  return Object.values(refs).reduce(
    (labels, elementId) => {
      const inputElement = elements[elementId];
      if (!inputElement || !hasDataProps(inputElement)) {
        return labels;
      }

      const binding = inputElement.props.data.find(
        (dataBinding) =>
          (isBindingActive(dataBinding) &&
            dataBinding.dataSource?.sourceType === 'formContainer') ||
          dataBinding.dataSource?.sourceType === 'editableList',
      );
      const column = binding?.dataSource?.columns[0];
      if (column) {
        const columnName = getColumnName(column);
        labels[columnName] =
          getValueByPath<string>(inputElement.props, 'content.title.label') ?? columnName;
      }
      return labels;
    },
    {} as Record<string, string>,
  );
}

/** The row count is carried in the error, so the message has to agree with it. */
function pluralizeRows(count: boolean): string {
  return (count as unknown as number) > 1 ? 'rows' : 'row';
}
